import { logger } from 'utils/logger';
import { appendLangParam } from 'helpers/reports_url_helper';
import { ConfigContext } from "App";
import AlertMessage from "Components/Common/Shared/AlertMesagge";
import BreadCrumb from "Components/Common/Shared/BreadCrumb";
import LoadingAnimation from "Components/Common/Shared/LoadingAnimation";
import { getEffectiveUser } from "helpers/impersonation_helper";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, Container, Modal, ModalBody, ModalHeader, Progress, Spinner } from "reactstrap";
import pigSilhouette from '../../assets/images/pig_silhouette.png'
import ObjectDetails from "Components/Common/Details/ObjectDetails";
import { Attribute } from "common/data_interfaces";
import AbortionForm from "../Forms/AbortionForm";
import BirthForm from "../Forms/BirthForm";
import PDFViewer from "Components/Common/Shared/PDFViewer";
import { useTranslation } from "react-i18next";

interface PregnancyDetailsProps {
    pregnancyId: string;
}

const isTablet = () => {
  const w = document.documentElement.clientWidth;
  return w >= 768 && w <= 1024;
};

const PregnancyDetails: React.FC<PregnancyDetailsProps> = ({ pregnancyId, }) => {
    const { t } = useTranslation();
    document.title = `${t("pregnancy.breadcrumb.title")} | ${t("systemName")}`;
    const configContext = useContext(ConfigContext);
    const userLogged = getEffectiveUser();
    const [pregnancyDetails, setPregnancyDetails] = useState<any>({});
    const [sowDetails, setSowDetails] = useState<any>({})
    const [inseminationDetails, setInseminationDetails] = useState<any>({})
    const [loading, setLoaging] = useState<boolean>(true)
    const [alertConfig, setAlertConfig] = useState({ visible: false, color: '', message: '' })
    const navigate = useNavigate();
    const [modals, setModals] = useState({ abortion: false, birth: false, viewPDF: false });
    const [tabletMode, setTabletMode] = useState(isTablet);
    const [allPregnancyData, setAllPregnancyData] = useState<any>({})
    const [pdfLoading, setPdfLoading] = useState(false);
    const [fileURL, setFileURL] = useState<string | null>(null);

    const toggleModal = (modalName: keyof typeof modals, state?: boolean) => {
        setModals((prev) => ({ ...prev, [modalName]: state ?? !prev[modalName] }));
    };

    const getFarrowingStatusConfig = (status: string) => {
        switch (status) {
            case "pregnant":
                return { color: "success", text: t('pregnancy.detail.statusPregnant', { defaultValue: 'Gestando' }) };
            case "close_to_farrow":
                return { color: "warning", text: t('pregnancy.detail.statusCloseToFarrow', { defaultValue: 'Próxima a parir' }) };
            case "farrowing_pending":
                return { color: "info", text: t('pregnancy.detail.statusFarrowingPending', { defaultValue: 'Parto pendiente' }) };
            case "overdue_farrowing":
                return { color: "danger", text: t('pregnancy.detail.statusOverdueFarrowing', { defaultValue: 'Parto atrasado' }) };
            case "farrowed":
                return { color: "dark", text: t('pregnancy.detail.statusFarrowed', { defaultValue: 'Parida' }) };
            case "abortion":
                return { color: "dark", text: t('pregnancy.detail.statusAbortion', { defaultValue: 'Aborto' }) };
            default:
                return { color: "secondary", text: t('pregnancy.detail.statusUnknown', { defaultValue: 'Desconocido' }) };
        }
    };

    const sowAttributes: Attribute[] = [
        { key: "code", label: t('common.field.code', { defaultValue: 'Código' }), type: "text" },
        { key: "birthdate", label: t('common.field.birthDate', { defaultValue: 'Fecha de nacimiento' }), type: "date" },
        { key: "breed", label: t('common.field.breed', { defaultValue: 'Raza' }), type: "text" },
        { key: "origin", label: t('pigs.field.origin', { defaultValue: 'Origen' }), type: "text" },
        { key: "weight", label: t('common.field.weightCurrent', { defaultValue: 'Peso actual' }), type: "text" },
        {
            key: "status",
            label: t('common.field.status', { defaultValue: 'Estado' }),
            type: "text",
            render: (value: string) => {
                const color = value === 'alive' ? 'success' : value === 'discarded' ? 'warning' : value === 'dead' ? 'danger' : 'secondary';
                const label = t(`pigs.status.${value}`, { defaultValue: value });
                return <Badge color={color}>{label}</Badge>;
            },
        },
        { key: "observations", label: t('pigs.field.observations', { defaultValue: 'Observaciones' }), type: "text" },
    ]

    const inseminationAttributes: Attribute[] = [
        { key: 'date', label: t('insemination.detail.attr.date', { defaultValue: 'F. inseminación' }), type: 'date' },
        { key: 'notes', label: t('insemination.detail.attr.notes', { defaultValue: 'Notas' }), type: 'text' },
        {
            key: 'estimated_farrowing_date',
            label: t('insemination.detail.attr.estimatedFarrowing', { defaultValue: 'Parto est.' }),
            type: 'date',
            render: (_: any, obj: any) => {
                const baseDate = new Date(obj.date);
                const estimated = new Date(baseDate.getTime() + 115 * 24 * 60 * 60 * 1000);
                return estimated.toLocaleDateString();
            }
        },
        {
            key: "result",
            label: t('insemination.column.result', { defaultValue: 'Resultado' }),
            type: "text",
            render: (value: string) => {
                const color = value === 'pregnant' ? 'success' : value === 'doubtful' ? 'warning' : value === 'empty' ? 'danger' : value === 'resorption' ? 'warning' : value === 'abortion' ? 'danger' : 'secondary';
                const label = t(`pregnancy.detail.result${value.charAt(0).toUpperCase() + value.slice(1)}`, { defaultValue: value });
                return <Badge color={color}>{label}</Badge>;
            },
        },
        {
            key: 'doses',
            label: t('insemination.detail.card.doses', { defaultValue: 'Dosis aplicadas' }),
            type: 'text',
            render: (_: any, obj: any) => <span className="text-black">{inseminationDetails.doses.length}</span>
        },
    ]

    const fetchData = async () => {
        if (!configContext || !userLogged || !pregnancyId) return
        try {
            const pregnancyResponse = await configContext.axiosHelper.get(`${configContext.apiUrl}/pregnancies/find_by_id/${pregnancyId}`)
            const pregnancyData = pregnancyResponse.data.data;
            setAllPregnancyData(pregnancyData)
            setSowDetails(pregnancyData.sow)
            setInseminationDetails(pregnancyData.insemination)

            const { sow, insemination, ...restPregnancy } = pregnancyData
            setPregnancyDetails(restPregnancy)
        } catch (error) {
            logger.error('Error fetching data:', { error })
            setAlertConfig({ visible: true, color: 'danger', message: t('pregnancy.detail.errorLoad', { defaultValue: 'Ha ocurrido un error al obtener los datos, intentelo mas tarde' }) })
        } finally {
            setLoaging(false)
        }
    }

    const handlePrintPregnancy = async () => {
        if (!configContext) return;

        try {
            setPdfLoading(true);
            const response = await configContext.axiosHelper.getBlob(appendLangParam(`${configContext.apiUrl}/reports/pregnancies/${pregnancyId}`));
            const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(pdfBlob);
            setFileURL(url);
            toggleModal('viewPDF');
        } catch (error) {
            logger.error('Error generating PDF: ', { error });
            setAlertConfig({ visible: true, color: 'danger', message: t('pregnancy.detail.errorPdf', { defaultValue: 'Error al generar el PDF, intentelo más tarde' }) });
        } finally {
            setPdfLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        const onResize = () => setTabletMode(isTablet());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [])


    if (loading) {
        return (
            <LoadingAnimation />
        )
    }

    return (
        <div className="">
            <Card className="shadow-sm h-100">
                <CardHeader className="d-flex justify-content-between align-items-center bg-light fs-5">
                    <h5>{t('pregnancy.detail.pregnancyProgress', { defaultValue: 'Progreso del embarazo' })}</h5>

                    <div className="d-flex gap-2">
                        <Button
                            color="primary"
                            className="btn-pdf"
                            onClick={handlePrintPregnancy}
                            disabled={pdfLoading}
                        >
                            {pdfLoading ? (
                                <>
                                    <Spinner className="me-2" size='sm' />
                                    {t('pregnancy.detail.generating', { defaultValue: 'Generando...' })}
                                </>
                            ) : (
                                <>
                                    <i className="ri-file-pdf-line me-2"></i>
                                    {t('pregnancy.detail.actionViewPdf', { defaultValue: 'Ver PDF' })}
                                </>
                            )}
                        </Button>

                        {pregnancyDetails.farrowing_status !== 'farrowed' && pregnancyDetails.farrowing_status !== 'abortion' && (
                            <Button color="danger" onClick={() => toggleModal('abortion')}>
                                <i className="bx bxs-skull me-2 align-middle fs-5" />
                                {t('pregnancy.detail.actionRegisterLoss', { defaultValue: 'Registrar perdida' })}
                            </Button>
                        )}

                        <Button onClick={() => toggleModal('birth')} disabled={pregnancyDetails.farrowing_status === 'farrowed' || pregnancyDetails.farrowing_status === 'pregnant' || pregnancyDetails.farrowing_status === 'abortion'}>
                            <i className="bx bx-dna align-middle me-2 fs-5" />
                            {t('pregnancy.detail.actionRegisterBirth', { defaultValue: 'Registrar parto' })}
                        </Button>
                    </div>


                </CardHeader>
                <CardBody>
                    <div className="d-flex flex-column justify-content-between align-items-center h-100">
                        {pregnancyDetails.start_date && (
                            <div className="w-100">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span className="fs-5 text-black">
                                        {t('pregnancy.detail.inseminationLabel', { defaultValue: 'Inseminación:' })}{" "}
                                        {new Date(pregnancyDetails.start_date).toLocaleDateString()}
                                    </span>

                                    <Badge
                                        className="fs-5"
                                        color={getFarrowingStatusConfig(pregnancyDetails.farrowing_status).color}
                                        pill
                                    >
                                        {getFarrowingStatusConfig(pregnancyDetails.farrowing_status).text}
                                    </Badge>

                                    <span className="fs-5 text-black">
                                        {t('pregnancy.detail.estimatedBirth', { defaultValue: 'Parto estimado:' })}{" "}
                                        {pregnancyDetails.estimated_farrowing_date === null ? (
                                            <>
                                                <span className="text-black">N/A</span>
                                            </>
                                        ) : (
                                            <>
                                                {new Date(pregnancyDetails.estimated_farrowing_date).toLocaleDateString()}
                                            </>
                                        )}
                                    </span>
                                </div>

                                <Progress
                                    value={(() => {
                                        const status = pregnancyDetails.farrowing_status;

                                        if (status === "abortion" || status === "farrowed") return 100;

                                        const start = new Date(pregnancyDetails.start_date);
                                        const end = new Date(pregnancyDetails.estimated_farrowing_date);
                                        const today = new Date();

                                        const totalMs = end.getTime() - start.getTime();
                                        const elapsedMs = today.getTime() - start.getTime();

                                        let percent = (elapsedMs / totalMs) * 100;
                                        if (percent < 0) percent = 0;
                                        if (percent > 100) percent = 100;

                                        return percent;
                                    })()}
                                    color={(() => {
                                        const status = pregnancyDetails.farrowing_status;
                                        if (status === "abortion" || status === "farrowed") return "dark";
                                        return getFarrowingStatusConfig(status).color;
                                    })()}
                                    className="animated-progess progress-xl"
                                />
                            </div>
                        )}
                    </div>
                </CardBody>
            </Card>

            <div className="d-flex gap-3 w-100 h-50">
                <Card className="w-100" >
                    <CardHeader className="d-flex justify-content-between align-items-center bg-light fs-5">
                        <span className="text-black fs-5">{t('pregnancy.detail.sowInseminated', { defaultValue: 'Cerda inseminada' })}</span>
                        <Button color='link' onClick={() => navigate(`/pigs/pig_details/${inseminationDetails.sow}`)}>
                            {t('pregnancy.detail.allInfo', { defaultValue: 'Toda la informacion ↗' })}
                        </Button>
                    </CardHeader>
                    <CardBody className="flex-fill">
                        <ObjectDetails attributes={sowAttributes} object={sowDetails} />
                    </CardBody>
                </Card>

                <Card className="w-100" >
                    <CardHeader className="d-flex justify-content-between align-items-center bg-light fs-5">
                        <span className="text-black fs-5">{t('pregnancy.detail.insemination', { defaultValue: 'Inseminación' })}</span>
                        <Button color='link' onClick={() => navigate(`/gestation/insemination_details/${inseminationDetails._id}`)}>
                            {t('pregnancy.detail.allInfo', { defaultValue: 'Toda la informacion ↗' })}
                        </Button>
                    </CardHeader>
                    <CardBody className="flex-fill">
                        <ObjectDetails attributes={inseminationAttributes} object={inseminationDetails} />
                    </CardBody>
                </Card>
            </div>

            <Modal size="lg" isOpen={modals.abortion} toggle={() => toggleModal("abortion")} backdrop="static" keyboard={false} centered fullscreen={tabletMode}>
                <ModalHeader toggle={() => toggleModal("abortion")}>{t('pregnancy.detail.modalLossTitle', { defaultValue: 'Registrar perdida' })}</ModalHeader>
                <ModalBody>
                    <AbortionForm pregnancy={{ _id: pregnancyId }} onSave={() => { toggleModal('abortion'); fetchData() }} onCancel={function (): void {
                        throw new Error("Function not implemented.");
                    }} />
                </ModalBody>
            </Modal>

            <Modal size="lg" isOpen={modals.birth} toggle={() => toggleModal("birth")} backdrop="static" keyboard={false} centered fullscreen={tabletMode}>
                <ModalHeader toggle={() => toggleModal("birth")}>{t('pregnancy.detail.modalBirthTitle', { defaultValue: 'Registrar parto' })}</ModalHeader>
                <ModalBody>
                    <BirthForm pregnancy={allPregnancyData} onSave={() => { toggleModal('birth'); fetchData(); }} onCancel={() => { }} />
                </ModalBody>
            </Modal>

            {/* Modal PDF */}
            <Modal size="xl" isOpen={modals.viewPDF} toggle={() => toggleModal("viewPDF")} backdrop='static' keyboard={false} centered fullscreen={true}>
                <ModalHeader toggle={() => toggleModal("viewPDF")}>{t('pregnancy.detail.modalPdfTitle', { defaultValue: 'Reporte de embarazo' })}</ModalHeader>
                <ModalBody>
                    {fileURL && <PDFViewer fileUrl={fileURL} />}
                </ModalBody>
            </Modal>

        </div>
    )
}

export default PregnancyDetails;
